// Worker slots: how many steps of a workspace's workflows, tasks and jobs
// run at once (state.json `worker_slots`, 1 to 48, absent = 8). The
// workspace reads its number from the capacity route, which also says what
// holds the slots now (GET …/workflows/capacity); Corvus sets it per
// workspace in Admin → a workspace → Features (GET/PUT
// /api/admin/customers/{c}/worker-slots {slots}, null = the default).
// A save refreshes both, and the settings view that carries it read-only.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DEFAULT_WORKER_SLOTS } from "@/features/workflows/workerSlotLimits";
import { apiFetch } from "@/lib/api";

import { adminCustomerPath } from "./adminFleet";

/** One run holding slots now. */
export interface CapacityRun {
  run_id: string;
  workflow: string;
  name: string;
  count: number;
}

/** GET /api/customers/{c}/workflows/capacity. */
export interface WorkflowCapacity {
  slots: number;
  in_use: number;
  runs: CapacityRun[];
  /** The workspace may boost a live run's steps at once (POST …/workflow-runs/{run}/boost); absent = no. */
  boost?: boolean;
  /** The most steps at once a boost may set (48 when absent). */
  boost_max?: number;
  /** Edit by chat and Fix and resume (state.json `workflow_assist_enabled`); absent = no. */
  assist?: boolean;
  /** Subagents are sandboxed here, so a step's own tools can hold; absent = unknown (an older api). */
  sandbox?: boolean;
  /** Data a workflow gives works only in its steps, report writer included (`workflow_data_in_runs_enabled`); absent = no. */
  data_in_runs?: boolean;
  /** A step's own time limit governs it, up to the workspace's; a subagent's holds when it is asked directly (`step_time_limits_enabled`); absent = no. */
  step_time_limits?: boolean;
  /** Deployments (workflows as APIs) are on for the workspace (`deployments_enabled`); absent = unknown (an older api). */
  deployments?: boolean;
  /** Deploy from words is in the image and Deployments are on (P6): Deploy, Change… and Go back show; absent = no. */
  deploy_words?: boolean;
}

/** GET/PUT /api/admin/customers/{c}/worker-slots: null = the default (8). */
export interface WorkerSlotsView {
  slots: number | null;
  /** On the boost whitelist: its operator boosts running workflows from the run page. */
  boost_enabled?: boolean;
}

export const workerSlotsKeys = {
  capacity: (c: string | null) => ["workflows", c, "capacity"] as const,
  admin: (c: string) => ["admin-worker-slots", c] as const,
};

/** The workspace's worker slots and who holds them; read every 10 s while `live` (a run page). */
export function useWorkflowCapacity(customerId: string | null | undefined, { live = false, enabled = true }: { live?: boolean; enabled?: boolean } = {}) {
  return useQuery({
    queryKey: workerSlotsKeys.capacity(customerId ?? null),
    queryFn: ({ signal }) => apiFetch<WorkflowCapacity>(`/api/customers/${encodeURIComponent(customerId ?? "")}/workflows/capacity`, { signal }),
    enabled: enabled && Boolean(customerId),
    refetchInterval: live ? 10_000 : false,
    staleTime: live ? 5_000 : 30_000,
    retry: false,
  });
}

/** The workspace's switches the pages read from the capacity route (false while unknown). */
export interface CapacityFlags {
  /** `step_time_limits`: a step runs its own time, a subagent's own limit holds only when asked directly. */
  stepTime: boolean;
  /** `data_in_runs`: what a workflow gives works only while it runs. */
  dataInRuns: boolean;
}

export function capacityFlags(c: WorkflowCapacity | null | undefined): CapacityFlags {
  return { stepTime: c?.step_time_limits === true, dataInRuns: c?.data_in_runs === true };
}

export function useCapacityFlags(customerId: string | null | undefined): CapacityFlags {
  return capacityFlags(useWorkflowCapacity(customerId).data);
}

/** The workspace's worker slots; the default until (or unless) the capacity route answers. */
export function useWorkerSlots(customerId: string | null): number {
  return useWorkflowCapacity(customerId).data?.slots ?? DEFAULT_WORKER_SLOTS;
}

export function useAdminWorkerSlots(customerId: string) {
  return useQuery({
    queryKey: workerSlotsKeys.admin(customerId),
    queryFn: ({ signal }) => apiFetch<WorkerSlotsView>(adminCustomerPath(customerId, "worker-slots"), { signal }),
    enabled: Boolean(customerId),
  });
}

/** Admin: put the workspace on (or off) the boost whitelist; the slots stay as they are. */
export function useSetBoost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, on }: { customerId: string; on: boolean }) =>
      apiFetch<WorkerSlotsView>(adminCustomerPath(customerId, "worker-slots"), { method: "PUT", body: { boost_enabled: on } }),
    onSuccess: (view, { customerId }) => {
      qc.setQueryData(workerSlotsKeys.admin(customerId), view);
      void qc.invalidateQueries({ queryKey: workerSlotsKeys.capacity(customerId) });
    },
  });
}

export function useSetWorkerSlots() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, slots }: { customerId: string; slots: number | null }) =>
      apiFetch<WorkerSlotsView>(adminCustomerPath(customerId, "worker-slots"), { method: "PUT", body: { slots } }),
    onSuccess: (view, { customerId }) => {
      qc.setQueryData(workerSlotsKeys.admin(customerId), view);
      void qc.invalidateQueries({ queryKey: workerSlotsKeys.capacity(customerId) });
      void qc.invalidateQueries({ queryKey: ["customer-settings", customerId] });
    },
  });
}
